<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { TextWidget } from '../../types/widget'

const props = defineProps<{ widget: TextWidget }>()
const emit = defineEmits<{
  (e: 'update', value: string): void
  /** 用户拖拽改变文本域高度（= 改变节点高度） */
  (e: 'resize', height: number): void
}>()

function onInput(ev: Event) {
  const t = ev.target as HTMLInputElement | HTMLTextAreaElement
  emit('update', t.value)
}

/*
 * 多行文本域开了原生 resize: vertical，但原生拖拽**不触发任何事件** ——
 * 所以用 ResizeObserver 捕捉尺寸变化并回写进模型，
 * 这样高度会跟着模板保存，重新渲染也不会丢。
 */
const area = ref<HTMLTextAreaElement | null>(null)
let observer: ResizeObserver | null = null

onMounted(() => {
  if (!props.widget.multiline || !area.value) return
  observer = new ResizeObserver((entries) => {
    const el = entries[0]?.target as HTMLElement | undefined
    // offsetHeight 是**布局像素**（不含画布缩放），正是我们要存的
    const height = Math.round(el?.offsetHeight ?? 0)
    if (height > 0 && Math.abs(height - (props.widget.height ?? 0)) > 1) {
      emit('resize', height)
    }
  })
  observer.observe(area.value)
})

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <div
    class="np-widget np-widget--text nodrag"
    :class="{ 'is-multiline': !!props.widget.multiline }"
    :title="props.widget.hint"
  >
    <textarea
      v-if="props.widget.multiline"
      ref="area"
      class="np-widget__field nowheel"
      :rows="props.widget.rows ?? 3"
      :style="
        props.widget.height ? { height: `${props.widget.height}px` } : undefined
      "
      :value="props.widget.value"
      :placeholder="props.widget.placeholder"
      :disabled="props.widget.disabled"
      spellcheck="false"
      @input="onInput"
      @pointerdown.stop
    />
    <input
      v-else
      class="np-widget__field"
      type="text"
      autocomplete="off"
      spellcheck="false"
      :value="props.widget.value"
      :placeholder="props.widget.placeholder"
      :disabled="props.widget.disabled"
      @input="onInput"
      @pointerdown.stop
    />
  </div>
</template>
